import type { AppointmentDetails } from '../api/payment';
import { normalizeCurrencyCode } from './formatPaymentAmount';
import type { EventTypeHint } from './roundRobin';

export type AppointmentSummary = {
  title: string;
  date: string;
  time_from: string;
  time_to: string;
  timeZoneName: string;
  servedAt: string;
  served_at_value?: number;
  served_at_data?: string;
  price: string | number;
  currency?: string;
  currency_label?: string;
  host: string;
  visible_to: number;
  description: string;
  duration?: string | number;
  hide_duration?: boolean;
  rescheduled?: boolean;
  appointment?: EventTypeHint | null;
  ics?: string;
  appointment_nr?: string;
  appointmentId?: string;
  email?: string;
  oldAppointment?: Record<string, unknown> | null;
  access_token?: string;
  has_e_payment?: boolean;
};

export function isSummaryComplete(
  summary: Partial<AppointmentSummary> | null | undefined
): boolean {
  if (!summary) {
    return false;
  }

  return Boolean(
    summary.title && summary.date && summary.time_from && summary.time_to
  );
}

export function mergeAppointmentSummary(
  ...sources: Array<Partial<AppointmentSummary> | null | undefined>
): AppointmentSummary | null {
  const merged = sources.reduce<Partial<AppointmentSummary>>((acc, source) => {
    if (!source) {
      return acc;
    }

    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key as keyof AppointmentSummary] = value as never;
      }
    });

    return acc;
  }, {});

  return isSummaryComplete(merged) ? (merged as AppointmentSummary) : null;
}

export function mapStoredContextToSummary(
  context: Record<string, unknown> | null | undefined
): Partial<AppointmentSummary> | null {
  if (!context) {
    return null;
  }

  const visibleTo = Number(
    context.visible_to ??
      (context as { visible_to_value?: number }).visible_to_value ??
      0
  );

  return {
    title: String(context.title ?? ''),
    description: String(context.description ?? ''),
    date: String(context.date ?? ''),
    time_from: String(context.time_from ?? ''),
    time_to: String(context.time_to ?? ''),
    timeZoneName: String(context.timeZoneName ?? ''),
    servedAt: String(context.servedAt ?? ''),
    served_at_value:
      typeof context.served_at_value === 'number'
        ? context.served_at_value
        : undefined,
    served_at_data:
      typeof context.served_at_data === 'string'
        ? context.served_at_data
        : undefined,
    price: (context.price as string | number | undefined) ?? '',
    currency: normalizeCurrencyCode(context.currency),
    currency_label:
      typeof context.currency_label === 'string' &&
      context.currency_label.trim() !== ''
        ? context.currency_label.trim()
        : undefined,
    host: String(context.host ?? ''),
    visible_to: Number.isFinite(visibleTo) ? visibleTo : 0,
    duration: context.duration as string | number | undefined,
    hide_duration: Boolean(context.hide_duration),
    rescheduled: Boolean(context.rescheduled),
    appointment: context.appointment as EventTypeHint | null | undefined,
    ics: typeof context.ics === 'string' ? context.ics : undefined,
    appointment_nr:
      typeof context.appointment_nr === 'string'
        ? context.appointment_nr
        : undefined,
    appointmentId:
      typeof context.appointmentId === 'string'
        ? context.appointmentId
        : typeof context.appointment_nr === 'string'
        ? context.appointment_nr
        : undefined,
    email: typeof context.email === 'string' ? context.email : undefined,
    oldAppointment: context.oldAppointment as Record<string, unknown> | null | undefined,
    access_token:
      typeof context.access_token === 'string'
        ? context.access_token
        : undefined,
  };
}

export function mapAppointmentDetailsToSummary(
  details: AppointmentDetails,
  fallback?: Partial<AppointmentSummary> | null
): Partial<AppointmentSummary> {
  const mapped: Partial<AppointmentSummary> = {
    title: details.title ?? fallback?.title ?? '',
    description: details.description ?? fallback?.description ?? '',
    date: details.date || fallback?.date || '',
    time_from: details.time_from || fallback?.time_from || '',
    time_to: details.time_to || fallback?.time_to || '',
    timeZoneName: details.time_zone?.name ?? fallback?.timeZoneName ?? '',
    servedAt: details.served_at ?? fallback?.servedAt ?? '',
    served_at_value: details.served_at_value ?? fallback?.served_at_value,
    served_at_data: details.served_at_data ?? fallback?.served_at_data,
    price: details.price ?? fallback?.price ?? '',
    currency:
      normalizeCurrencyCode(details.currency) ??
      normalizeCurrencyCode(fallback?.currency),
    currency_label: details.currency_label ?? fallback?.currency_label,
    host: details.host ?? fallback?.host ?? '',
    visible_to: details.visible_to_value ?? fallback?.visible_to ?? 0,
    duration: details.duration ?? fallback?.duration,
    hide_duration: details.hide_duration ?? fallback?.hide_duration,
    rescheduled: details.rescheduled ?? fallback?.rescheduled ?? false,
    ics: details.ics_file_path ?? fallback?.ics,
    appointment_nr: details.appointment_nr ?? fallback?.appointment_nr,
    appointmentId: details.appointment_nr ?? fallback?.appointmentId,
    email: details.customer_email ?? fallback?.email,
    appointment:
      fallback?.appointment ??
      (details.event_type_value != null
        ? { type_value: details.event_type_value }
        : undefined),
    oldAppointment: fallback?.oldAppointment,
    access_token: fallback?.access_token,
    has_e_payment: details.has_e_payment ?? fallback?.has_e_payment,
  };

  return mapped;
}

export function mapSummaryToAppointmentData(
  summary: AppointmentSummary
): Record<string, unknown> {
  return {
    ...summary,
    visible_to: summary.visible_to,
    has_e_payment: summary.has_e_payment,
  };
}
